import type {
  DropdownQuestion,
  NumberQuestion,
  PhoneQuestion,
  Question,
  SelectQuestion,
  TextQuestion,
} from './types';
import { formatPhoneAsTyping } from './validators';

export interface StepCallbacks {
  onValueChange(fieldName: string, value: string): void;
  onConsentChange?(consented: boolean): void;
  onNext(): void;
  onBack(): void;
  onSubmit(): void;
}

/** Used as the contract for keeping form-state in sync with the rendered step. */
export interface StepHandle {
  element: HTMLElement;
  question: Question;
  setValid(valid: boolean, message: string): void;
  enableSubmit(enabled: boolean): void;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
] as const;

function eyebrowFor(question: Question, totalSteps: number): string {
  if (question.step === totalSteps) return 'Final Step';
  const padded = String(question.step).padStart(2, '0');
  return `Question ${padded} of ${String(totalSteps).padStart(2, '0')}`;
}

function emphasizeQuestion(text: string): string {
  // Wrap a likely emphasized phrase in <em> for the gold accent style.
  // Fallback: italicize the last 2 words.
  const stripped = text.trim().replace(/[?]+$/, '');
  const tokens = stripped.split(/\s+/);
  if (tokens.length <= 2) {
    return `<em>${escapeHtml(stripped)}</em>?`;
  }
  const head = tokens.slice(0, -2).join(' ');
  const tail = tokens.slice(-2).join(' ');
  return `${escapeHtml(head)} <em>${escapeHtml(tail)}</em>?`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function choiceIconFor(option: string, index: number): string {
  if (option.startsWith('$')) return String(index + 1);
  if (/^(yes)$/i.test(option)) return '✓';
  if (/^(no)$/i.test(option)) return '✕';
  const words = option.split(/[\s/]+/).filter(Boolean);
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  return words
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('');
}

function buildShell(question: Question, totalSteps: number): {
  step: HTMLElement;
  body: HTMLElement;
  nav: HTMLElement;
} {
  const step = document.createElement('div');
  step.className = 'step';
  step.dataset.step = String(question.step);
  step.dataset.field = question.field_name;
  step.dataset.type = question.type;

  const eyebrow = document.createElement('div');
  eyebrow.className = 'step-eyebrow';
  eyebrow.textContent = eyebrowFor(question, totalSteps);
  step.appendChild(eyebrow);

  const heading = document.createElement('h2');
  heading.className = 'step-question';
  heading.innerHTML = emphasizeQuestion(question.question);
  step.appendChild(heading);

  const help = document.createElement('p');
  help.className = 'step-help';
  help.textContent = question.subtitle ?? defaultHelpFor(question);
  step.appendChild(help);

  const body = document.createElement('div');
  body.className = 'step-body';
  step.appendChild(body);

  const nav = document.createElement('div');
  nav.className = 'step-nav';
  step.appendChild(nav);

  return { step, body, nav };
}

function defaultHelpFor(question: Question): string {
  switch (question.type) {
    case 'tel': return 'A licensed Catalyst agent will reach out shortly with your personalized options.';
    case 'email': return 'Your information is private and never sold.';
    case 'text': return 'Enter your full legal name.';
    case 'number': return 'Coverage and rate options vary by age.';
    case 'dropdown': return 'We will match you with an agent licensed in your state.';
    default: return 'Select the option that best describes you.';
  }
}

function renderSelect(question: SelectQuestion, body: HTMLElement, callbacks: StepCallbacks): {
  validate: (value: string) => void;
} {
  const choices = document.createElement('div');
  choices.className = 'choices';
  choices.dataset.field = question.field_name;
  body.appendChild(choices);

  question.options.forEach((option, index) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'choice';
    btn.dataset.value = option;

    const icon = document.createElement('span');
    icon.className = 'choice-icon';
    icon.textContent = choiceIconFor(option, index);
    btn.appendChild(icon);
    btn.append(option);

    btn.addEventListener('click', () => {
      choices.querySelectorAll('.choice').forEach(c => c.classList.remove('selected'));
      btn.classList.add('selected');
      callbacks.onValueChange(question.field_name, option);
    });

    choices.appendChild(btn);
  });

  return {
    validate: () => { /* selection validity is binary; handled in main */ },
  };
}

function renderTextInput(
  question: TextQuestion | NumberQuestion,
  body: HTMLElement,
  callbacks: StepCallbacks,
): { input: HTMLInputElement; statusEl: HTMLElement } {
  const field = document.createElement('div');
  field.className = 'field';

  const label = document.createElement('label');
  const inputId = `field-${question.field_name}`;
  label.htmlFor = inputId;
  label.textContent = question.label ?? question.question;
  field.appendChild(label);

  const input = document.createElement('input');
  input.id = inputId;
  input.name = question.field_name;
  if (question.type === 'email') {
    input.type = 'email';
    input.autocomplete = 'email';
    input.placeholder = 'you@example.com';
  } else if (question.type === 'number') {
    input.type = 'number';
    input.inputMode = 'numeric';
    input.min = String(question.validation.min);
    input.max = String(question.validation.max);
    input.placeholder = `${question.validation.min} – ${question.validation.max}`;
  } else {
    input.type = 'text';
    input.autocomplete = 'name';
    input.placeholder = 'Jane Doe';
  }
  field.appendChild(input);

  const statusEl = document.createElement('div');
  statusEl.className = 'field-status';
  field.appendChild(statusEl);

  body.appendChild(field);

  input.addEventListener('input', () => {
    callbacks.onValueChange(question.field_name, input.value);
  });
  input.addEventListener('blur', () => {
    callbacks.onValueChange(question.field_name, input.value);
  });

  return { input, statusEl };
}

function renderDropdown(
  question: DropdownQuestion,
  body: HTMLElement,
  callbacks: StepCallbacks,
): { input: HTMLSelectElement; statusEl: HTMLElement } {
  const field = document.createElement('div');
  field.className = 'field';

  const label = document.createElement('label');
  const id = `field-${question.field_name}`;
  label.htmlFor = id;
  label.textContent = question.label ?? 'State of Residence';
  field.appendChild(label);

  const select = document.createElement('select');
  select.id = id;
  select.name = question.field_name;

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = 'Select your state...';
  select.appendChild(placeholder);

  for (const state of US_STATES) {
    const opt = document.createElement('option');
    opt.value = state;
    opt.textContent = state;
    select.appendChild(opt);
  }

  field.appendChild(select);

  const statusEl = document.createElement('div');
  statusEl.className = 'field-status';
  field.appendChild(statusEl);

  body.appendChild(field);

  select.addEventListener('change', () => {
    callbacks.onValueChange(question.field_name, select.value);
  });

  return { input: select, statusEl };
}

function renderPhone(
  question: PhoneQuestion,
  body: HTMLElement,
  callbacks: StepCallbacks,
): { input: HTMLInputElement; statusEl: HTMLElement; wrap: HTMLElement; consent: HTMLInputElement } {
  const field = document.createElement('div');
  field.className = 'field';

  const label = document.createElement('label');
  label.htmlFor = `field-${question.field_name}`;
  label.textContent = question.label ?? 'Phone Number (US)';
  field.appendChild(label);

  const wrap = document.createElement('div');
  wrap.className = 'phone-input-wrap';

  const prefix = document.createElement('span');
  prefix.className = 'phone-prefix';
  prefix.textContent = '+1';
  wrap.appendChild(prefix);

  const input = document.createElement('input');
  input.type = 'tel';
  input.id = `field-${question.field_name}`;
  input.name = question.field_name;
  input.autocomplete = 'tel-national';
  input.placeholder = '(555) 123-4567';
  input.maxLength = 14;
  wrap.appendChild(input);

  field.appendChild(wrap);

  const statusEl = document.createElement('div');
  statusEl.className = 'field-status';
  field.appendChild(statusEl);

  body.appendChild(field);

  // Consent checkbox
  const consentWrap = document.createElement('div');
  consentWrap.className = 'consent';

  const consent = document.createElement('input');
  consent.type = 'checkbox';
  consent.id = 'consent-checkbox';
  consentWrap.appendChild(consent);

  const consentLabel = document.createElement('label');
  consentLabel.htmlFor = 'consent-checkbox';
  consentLabel.textContent = question.consent_text;
  consentWrap.appendChild(consentLabel);

  body.appendChild(consentWrap);

  input.addEventListener('input', () => {
    input.value = formatPhoneAsTyping(input.value);
    callbacks.onValueChange(question.field_name, input.value);
  });
  input.addEventListener('blur', () => {
    callbacks.onValueChange(question.field_name, input.value);
  });
  consent.addEventListener('change', () => {
    callbacks.onConsentChange?.(consent.checked);
  });

  return { input, statusEl, wrap, consent };
}

function renderNav(
  isFirst: boolean,
  isLast: boolean,
  submitLabel: string,
  nav: HTMLElement,
  callbacks: StepCallbacks,
): HTMLButtonElement {
  if (!isFirst) {
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'btn btn-back';
    back.textContent = '← Back';
    back.addEventListener('click', callbacks.onBack);
    nav.appendChild(back);
  } else {
    const spacer = document.createElement('span');
    nav.appendChild(spacer);
  }

  const primary = document.createElement('button');
  primary.type = 'button';
  primary.className = 'btn btn-primary';
  primary.disabled = true;
  if (isLast) {
    primary.textContent = `${submitLabel} →`;
    primary.addEventListener('click', callbacks.onSubmit);
  } else {
    primary.textContent = 'Continue →';
    primary.addEventListener('click', callbacks.onNext);
  }
  nav.appendChild(primary);
  return primary;
}

export function renderStep(
  question: Question,
  totalSteps: number,
  callbacks: StepCallbacks,
): StepHandle {
  const { step, body, nav } = buildShell(question, totalSteps);

  let phoneRefs: ReturnType<typeof renderPhone> | null = null;
  let textRefs: { input: HTMLInputElement | HTMLSelectElement; statusEl: HTMLElement } | null = null;

  switch (question.type) {
    case 'single_select':
    case 'boolean':
      renderSelect(question, body, callbacks);
      break;
    case 'dropdown':
      textRefs = renderDropdown(question, body, callbacks);
      break;
    case 'text':
    case 'email':
      textRefs = renderTextInput(question, body, callbacks);
      break;
    case 'number':
      textRefs = renderTextInput(question, body, callbacks);
      break;
    case 'tel':
      phoneRefs = renderPhone(question, body, callbacks);
      break;
  }

  const isLast = question.type === 'tel';
  const isFirst = question.step === 1;
  const submitLabel = question.type === 'tel' ? question.submit_button : 'Submit';
  const primary = renderNav(isFirst, isLast, submitLabel, nav, callbacks);

  function setValid(valid: boolean, message: string) {
    if (phoneRefs) {
      const { wrap, statusEl, input } = phoneRefs;
      wrap.classList.remove('valid', 'invalid');
      statusEl.classList.remove('error', 'success');
      if (input.value === '') {
        statusEl.textContent = '';
        return;
      }
      if (valid) {
        wrap.classList.add('valid');
        statusEl.classList.add('success');
      } else {
        wrap.classList.add('invalid');
        statusEl.classList.add('error');
      }
      statusEl.textContent = message;
    } else if (textRefs) {
      const { input, statusEl } = textRefs;
      input.classList.remove('valid', 'invalid');
      statusEl.classList.remove('error', 'success');
      if (input.value === '') {
        statusEl.textContent = '';
        return;
      }
      if (valid) {
        input.classList.add('valid');
        if (message) {
          statusEl.classList.add('success');
          statusEl.textContent = message;
        } else {
          statusEl.textContent = '';
        }
      } else {
        input.classList.add('invalid');
        statusEl.classList.add('error');
        statusEl.textContent = message;
      }
    }
    // single_select / boolean: no inline status — selection state shown on the choice itself.
  }

  function enableSubmit(enabled: boolean) {
    primary.disabled = !enabled;
  }

  return {
    element: step,
    question,
    setValid,
    enableSubmit,
  };
}

